import { useCallback, useRef, useState } from "react";
import Cropper from "react-easy-crop";
import { jsPDF } from "jspdf";
import { api, apiError } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Slider } from "./ui/slider";
import { toast } from "sonner";
import { Camera, ImageIcon, RotateCw, ZoomIn, Sparkles, Loader2 } from "lucide-react";

function createImage(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.addEventListener("load", () => resolve(img));
    img.addEventListener("error", (e) => reject(e));
    img.src = url;
  });
}

function rotateSize(width, height, rotation) {
  const r = (rotation * Math.PI) / 180;
  return {
    width: Math.abs(Math.cos(r) * width) + Math.abs(Math.sin(r) * height),
    height: Math.abs(Math.sin(r) * width) + Math.abs(Math.cos(r) * height),
  };
}

function enhance(ctx, w, h) {
  const img = ctx.getImageData(0, 0, w, h);
  const d = img.data;
  const contrast = 1.4, brightness = 10;
  for (let i = 0; i < d.length; i += 4) {
    let v = 0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2];
    v = (v - 128) * contrast + 128 + brightness;
    v = v < 0 ? 0 : v > 255 ? 255 : v;
    d[i] = d[i + 1] = d[i + 2] = v;
  }
  ctx.putImageData(img, 0, 0);
}

async function processImage(imageSrc, pixelCrop, rotation, doEnhance) {
  const image = await createImage(imageSrc);
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  const { width: bw, height: bh } = rotateSize(image.width, image.height, rotation);
  canvas.width = bw;
  canvas.height = bh;
  ctx.translate(bw / 2, bh / 2);
  ctx.rotate((rotation * Math.PI) / 180);
  ctx.translate(-image.width / 2, -image.height / 2);
  ctx.drawImage(image, 0, 0);

  const out = document.createElement("canvas");
  const octx = out.getContext("2d");
  out.width = Math.max(1, Math.round(pixelCrop.width));
  out.height = Math.max(1, Math.round(pixelCrop.height));
  octx.drawImage(canvas, pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height,
    0, 0, out.width, out.height);
  if (doEnhance) enhance(octx, out.width, out.height);
  return { dataUrl: out.toDataURL("image/jpeg", 0.85), width: out.width, height: out.height };
}

function makePdfBlob(dataUrl, wpx, hpx) {
  const orientation = wpx >= hpx ? "landscape" : "portrait";
  const pdf = new jsPDF({ orientation, unit: "pt", format: "a4" });
  const pw = pdf.internal.pageSize.getWidth();
  const ph = pdf.internal.pageSize.getHeight();
  const m = 24;
  const ratio = Math.min((pw - m * 2) / wpx, (ph - m * 2) / hpx);
  const w = wpx * ratio, h = hpx * ratio;
  pdf.addImage(dataUrl, "JPEG", (pw - w) / 2, (ph - h) / 2, w, h);
  return pdf.output("blob");
}

const ASPECTS = [
  { l: "A4", v: 1 / Math.SQRT2 },
  { l: "Square", v: 1 },
  { l: "Wide", v: Math.SQRT2 },
];

export function ReceiptScanner({ open, onClose, onDone }) {
  const [stage, setStage] = useState("source"); // source | edit | preview
  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [aspect, setAspect] = useState(1 / Math.SQRT2);
  const [enhanceOn, setEnhanceOn] = useState(true);
  const [pixels, setPixels] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const camRef = useRef();
  const galRef = useRef();

  const reset = () => {
    setStage("source"); setImageSrc(null); setCrop({ x: 0, y: 0 });
    setZoom(1); setRotation(0); setAspect(1 / Math.SQRT2); setEnhanceOn(true);
    setPixels(null); setPreview(null); setBusy(false);
  };

  const close = () => { reset(); onClose(); };

  const onFile = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Please choose an image to scan."); return; }
    const reader = new FileReader();
    reader.onload = () => { setImageSrc(reader.result); setStage("edit"); };
    reader.readAsDataURL(f);
  };

  const onCropComplete = useCallback((_area, areaPixels) => setPixels(areaPixels), []);

  const toPreview = async () => {
    if (!pixels) return;
    setBusy(true);
    try {
      const res = await processImage(imageSrc, pixels, rotation, enhanceOn);
      setPreview(res);
      setStage("preview");
    } catch { toast.error("Could not process the image. Try again."); }
    setBusy(false);
  };

  const saveAsPdf = async () => {
    if (!preview) return;
    setBusy(true);
    try {
      const blob = makePdfBlob(preview.dataUrl, preview.width, preview.height);
      const file = new File([blob], `Receipt-${Date.now()}.pdf`, { type: "application/pdf" });
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Receipt saved");
      onDone(data);
      close();
    } catch (err) {
      // Keep the user on preview so they can retry without losing the scan.
      toast.error(apiError(err) || "Upload failed — please retry.");
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) close(); }}>
      <DialogContent data-testid="receipt-scanner" className="max-w-md p-0 gap-0 overflow-hidden">
        <DialogHeader className="px-5 pt-5 pb-3">
          <DialogTitle>Scan receipt</DialogTitle>
          <DialogDescription>
            {stage === "source" && "Take a photo or choose an image to scan."}
            {stage === "edit" && "Crop, rotate and zoom to frame the receipt."}
            {stage === "preview" && "Check readability, then save as a PDF."}
          </DialogDescription>
        </DialogHeader>

        {stage === "source" && (
          <div className="px-5 pb-6 space-y-3">
            <input ref={camRef} type="file" accept="image/*" capture="environment" onChange={onFile} className="hidden" data-testid="scanner-camera-input" />
            <input ref={galRef} type="file" accept="image/*" onChange={onFile} className="hidden" data-testid="scanner-gallery-input" />
            <Button data-testid="scanner-take-photo" onClick={() => camRef.current?.click()} className="w-full h-12 rounded-xl bg-[#0D786A] hover:bg-[#0A6055]">
              <Camera size={18} className="mr-2" /> Take Photo
            </Button>
            <Button data-testid="scanner-choose-photo" variant="outline" onClick={() => galRef.current?.click()} className="w-full h-12 rounded-xl">
              <ImageIcon size={18} className="mr-2" /> Choose Photo
            </Button>
          </div>
        )}

        {stage === "edit" && (
          <div className="pb-5">
            <div className="relative h-[46vh] bg-slate-900">
              <Cropper
                image={imageSrc} crop={crop} zoom={zoom} rotation={rotation} aspect={aspect}
                onCropChange={setCrop} onZoomChange={setZoom} onCropComplete={onCropComplete}
                showGrid restrictPosition={false}
              />
            </div>
            <div className="px-5 pt-4 space-y-4">
              <div className="flex gap-2">
                {ASPECTS.map((a) => (
                  <button key={a.l} type="button" data-testid={`scanner-aspect-${a.l.toLowerCase()}`} onClick={() => setAspect(a.v)}
                    className={`flex-1 h-9 rounded-lg border text-xs font-medium df-pressable ${Math.abs(aspect - a.v) < 0.001 ? "bg-[#0D786A] text-white border-[#0D786A]" : "bg-white text-slate-600 border-slate-200"}`}>{a.l}</button>
                ))}
                <button type="button" data-testid="scanner-rotate" onClick={() => setRotation((r) => (r + 90) % 360)}
                  className="h-9 px-3 rounded-lg border border-slate-200 bg-white text-slate-600 df-pressable inline-flex items-center gap-1 text-xs"><RotateCw size={14} /> Rotate</button>
              </div>
              <div className="flex items-center gap-3">
                <ZoomIn size={16} className="text-slate-400 shrink-0" />
                <Slider data-testid="scanner-zoom" value={[zoom]} min={1} max={3} step={0.05} onValueChange={(v) => setZoom(v[0])} className="flex-1" />
              </div>
              <button type="button" data-testid="scanner-enhance" onClick={() => setEnhanceOn((v) => !v)}
                className={`w-full h-10 rounded-xl border text-sm font-medium df-pressable inline-flex items-center justify-center gap-2 ${enhanceOn ? "bg-[#E6F4F1] text-[#0D786A] border-[#0D786A]/40" : "bg-white text-slate-600 border-slate-200"}`}>
                <Sparkles size={16} /> Improve readability {enhanceOn ? "· On" : "· Off"}
              </button>
              <div className="flex gap-3 pt-1">
                <Button variant="outline" onClick={() => setStage("source")} className="flex-1 h-11 rounded-xl">Retake</Button>
                <Button data-testid="scanner-next" onClick={toPreview} disabled={busy} className="flex-1 h-11 rounded-xl bg-[#0D786A] hover:bg-[#0A6055]">
                  {busy ? <Loader2 className="animate-spin" size={16} /> : "Preview"}
                </Button>
              </div>
            </div>
          </div>
        )}

        {stage === "preview" && preview && (
          <div className="px-5 pb-5 space-y-4">
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-2 max-h-[46vh] overflow-auto">
              <img src={preview.dataUrl} alt="Receipt preview" data-testid="scanner-preview-img" className="w-full rounded-lg" />
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setStage("edit")} className="flex-1 h-11 rounded-xl">Adjust</Button>
              <Button variant="outline" onClick={() => setStage("source")} className="flex-1 h-11 rounded-xl">Retake</Button>
            </div>
            <Button data-testid="scanner-save" onClick={saveAsPdf} disabled={busy} className="w-full h-12 rounded-xl bg-[#0D786A] hover:bg-[#0A6055]">
              {busy ? <><Loader2 className="animate-spin mr-2" size={16} /> Saving…</> : "Save as PDF"}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
